//also removed duplicate icons like refresh and teh mode button  
import { format } from "date-fns";
import { Calendar as CalendarIcon, ClipboardList, DollarSign, FileText, Package, Settings2, ShoppingCart, TrendingUp } from "lucide-react";
import { useGetIdentity, useGetList } from "ra-core";
import * as React from "react";

import { DocumentPipelineView } from "./DocumentPipelineView";
import { ExpenseTracker } from "./ExpenseTracker";
import { FinancialMetricCard } from "./FinancialMetricCard";
import { OutstandingInvoicesWidget } from "./OutstandingInvoicesWidget";
import { PaymentReminderCard } from "./PaymentReminderCard";
import { RecentActivityCard } from "./RecentActivityCard";
import { StockMovementWidget } from "./StockMovementWidget";
import { TrendChart } from "./TrendChart";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export const Dashboard = () => {
  const { data: identity } = useGetIdentity();
  const [date, setDate] = React.useState<Date | undefined>(new Date());
  const [period, setPeriod] = React.useState<'Daily' | 'Weekly' | 'Monthly' | 'Yearly'>('Monthly');

  // ─── Live Metrics ──────────────────────────────────────────────────────
  const { data: enquiries } = useGetList("enquiries", {
    pagination: { page: 1, perPage: 500 },
    filter: {},
  });
  const { data: projects } = useGetList("projects", {
    pagination: { page: 1, perPage: 500 },
    filter: {},
  });
  const { data: invoices } = useGetList("customer_invoices", {
    pagination: { page: 1, perPage: 500 },
    filter: {},
  });
  const { data: purchaseOrders } = useGetList("purchase_orders", {
    pagination: { page: 1, perPage: 500 },
    filter: {},
  });
  const { data: quotations } = useGetList("quotations", {
    pagination: { page: 1, perPage: 500 },
    filter: {},
  });
  const { data: workOrders } = useGetList("work_orders", {
    pagination: { page: 1, perPage: 500 },
    filter: {},
  });

  // ─── Calculated Metrics ─────────────────────────────────────────────────
  const activeEnquiries = enquiries?.filter((e) => e.status === "new" || e.status === "in_discussion").length ?? 0;
  const activeProjects = projects?.filter((p) => p.status === "active").length ?? 0;
  const pendingPOs = purchaseOrders?.filter((po) => po.status === "draft" || po.status === "sent").length ?? 0;
  const openWorkOrders = workOrders?.filter((wo) => wo.status === "open" || wo.status === "in_progress").length ?? 0;

  const totalRevenue = invoices?.reduce((sum, inv) => sum + (inv.total_amount || 0), 0) ?? 0;
  const paidRevenue = invoices?.reduce((sum, inv) => sum + (inv.paid_amount || 0), 0) ?? 0;
  const revenueProgress = totalRevenue > 0 ? Math.round((paidRevenue / totalRevenue) * 100) : 0;

  const totalPOValue = purchaseOrders?.reduce((sum, po) => sum + (po.total_amount || 0), 0) ?? 0;

  // ─── Trend Chart Data (dynamic based on period) ────────────
  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const now = new Date();

  const getEarningsData = () => {
    if (period === 'Daily') {
      return Array.from({ length: 14 }, (_, i) => {
        const date = new Date(now.getFullYear(), now.getMonth(), now.getDate() - (13 - i));
        const dayStr = date.toISOString().split("T")[0];
        const label = date.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
        const income = invoices?.filter((inv) => inv.date?.startsWith(dayStr)).reduce((s, inv) => s + (inv.total_amount || 0), 0) ?? 0;
        const expense = purchaseOrders?.filter((po) => po.date?.startsWith(dayStr)).reduce((s, po) => s + (po.total_amount || 0), 0) ?? 0;
        return { label, income, expense };
      });
    } else if (period === 'Weekly') {
      return Array.from({ length: 8 }, (_, i) => {
        const endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - (7 - i) * 7);
        endDate.setDate(endDate.getDate() + (6 - endDate.getDay())); // end of that week
        const startDate = new Date(endDate);
        startDate.setDate(endDate.getDate() - 6); // start of week
        const label = `Wk ${endDate.getDate()} ${monthNames[endDate.getMonth()]}`;
        const income = invoices?.filter((inv) => {
          const d = new Date(inv.date); return d >= startDate && d <= endDate;
        }).reduce((s, inv) => s + (inv.total_amount || 0), 0) ?? 0;
        const expense = purchaseOrders?.filter((po) => {
          const d = new Date(po.date); return d >= startDate && d <= endDate;
        }).reduce((s, po) => s + (po.total_amount || 0), 0) ?? 0;
        return { label, income, expense };
      });
    } else if (period === 'Yearly') {
      return Array.from({ length: 3 }, (_, i) => {
        const year = now.getFullYear() - (2 - i);
        const label = `${year}`;
        const income = invoices?.filter((inv) => new Date(inv.date).getFullYear() === year).reduce((s, inv) => s + (inv.total_amount || 0), 0) ?? 0;
        const expense = purchaseOrders?.filter((po) => new Date(po.date).getFullYear() === year).reduce((s, po) => s + (po.total_amount || 0), 0) ?? 0;
        return { label, income, expense };
      });
    } else { // Monthly
      return Array.from({ length: 6 }, (_, i) => {
        const date = new Date(now.getFullYear(), now.getMonth() - (5 - i), 1);
        const label = monthNames[date.getMonth()];
        const year = date.getFullYear();
        const m = date.getMonth();
        const income = invoices?.filter((inv) => {
          const d = new Date(inv.date); return d.getMonth() === m && d.getFullYear() === year;
        }).reduce((s, inv) => s + (inv.total_amount || 0), 0) ?? 0;
        const expense = purchaseOrders?.filter((po) => {
          const d = new Date(po.date); return d.getMonth() === m && d.getFullYear() === year;
        }).reduce((s, po) => s + (po.total_amount || 0), 0) ?? 0;
        return { label, income, expense };
      });
    }
  };
  const earningsData = getEarningsData();

  // ─── Expense Tracker (dynamic spend from POs) ──────────
  const getExpenseData = () => {
    if (period === 'Daily' || period === 'Weekly') {
      return Array.from({ length: 7 }, (_, i) => {
        const target = new Date(now.getFullYear(), now.getMonth(), now.getDate() - (6 - i));
        const dayStr = target.toISOString().split("T")[0];
        const category = target.toLocaleDateString("en-IN", { weekday: "short" });
        const amount = purchaseOrders?.filter((po) => po.date?.startsWith(dayStr)).reduce((s, po) => s + (po.total_amount || 0), 0) ?? 0;
        return { category, amount };
      });
    } else if (period === 'Yearly') {
      return Array.from({ length: 6 }, (_, i) => {
        const date = new Date(now.getFullYear(), now.getMonth() - (5 - i), 1);
        const category = monthNames[date.getMonth()];
        const year = date.getFullYear();
        const m = date.getMonth();
        const amount = purchaseOrders?.filter((po) => {
          const d = new Date(po.date); return d.getMonth() === m && d.getFullYear() === year;
        }).reduce((s, po) => s + (po.total_amount || 0), 0) ?? 0;
        return { category, amount };
      });
    } else { // Monthly
      return Array.from({ length: 4 }, (_, i) => {
        const endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - (3 - i) * 7);
        const startDate = new Date(endDate);
        startDate.setDate(endDate.getDate() - 6);
        const category = `Wk ${3 - i + 1} ago`;
        const amount = purchaseOrders?.filter((po) => {
          const d = new Date(po.date); return d >= startDate && d <= endDate;
        }).reduce((s, po) => s + (po.total_amount || 0), 0) ?? 0;
        return { category, amount };
      });
    }
  };
  const expenseData = getExpenseData();

  // ─── Recent Activities (last 8 records across invoices & POs) ────────
  const invoiceActivities = (invoices || []).slice(0, 4).map((inv) => ({
    id: Number(inv.id),
    type: "income" as const,
    description: `Invoice to ${inv.customer_name || "Customer"}`,
    amount: inv.total_amount || 0,
    date: inv.date ? new Date(inv.date).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "",
    category: `${inv.reference}`,
    link: `/customer_invoices/${inv.id}/show`,
  }));

  const poActivities = (purchaseOrders || []).slice(0, 4).map((po) => ({
    id: Number(po.id) + 10000,
    type: "expense" as const,
    description: `PO to ${po.vendor_name || "Vendor"}`,
    amount: po.total_amount || 0,
    date: po.date ? new Date(po.date).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "",
    category: `${po.reference}`,
    link: `/purchase_orders/${po.id}/show`,
  }));

  const recentActivities = [...invoiceActivities, ...poActivities]
    .sort((a, b) => (b.date > a.date ? 1 : -1))
    .slice(0, 6);

  // ─── Render ─────────────────────────────────────────────────────────────
  return (
    <div className="h-full overflow-y-auto scrollbar-hide">
      <div className="space-y-6 pt-10 pb-10">
        {/* Welcome Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex flex-col">
            <h1 className="text-3xl font-bold text-foreground">Dashboard</h1>
            <p className="text-muted-foreground mt-1 text-sm">
              Hello, {identity?.fullName}. Welcome back to your ERP Management System
            </p>
          </div>
          {/* Dashboard Top Actions */}
          <div className="flex items-center gap-3 bg-card/60 backdrop-blur-sm p-2 px-4 rounded-full border border-border shrink-0 shadow-sm">
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  className="h-9 justify-start font-normal rounded-full px-4 hover:bg-background/80"
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {date ? format(date, "PPP") : <span>Pick a date</span>}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 rounded-2xl border-border shadow-lg" align="end">
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={setDate}
                  initialFocus
                  className="rounded-2xl"
                  classNames={{
                    day: "h-9 w-9 p-0 font-normal rounded-full aria-selected:opacity-100 hover:bg-sidebar-accent transition-colors",
                    day_selected: "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground",
                    day_today: "font-semibold",
                    head_cell: "text-muted-foreground rounded-md w-9 font-normal text-[0.8rem]",
                  }}
                />
              </PopoverContent>
            </Popover>
            <div className="h-6 w-px bg-border/50 mx-1 hidden md:block"></div>
            <div className="flex items-center gap-1 bg-muted/50 p-1 rounded-full border border-border/50">
              {(['Daily', 'Weekly', 'Monthly', 'Yearly'] as const).map((p) => (
                <button
                  key={p}
                  onClick={() => setPeriod(p)}
                  className={`px-3 py-1 text-xs font-semibold rounded-full transition-all ${period === p
                    ? 'bg-background text-primary shadow-sm'
                    : 'text-muted-foreground hover:text-primary hover:bg-background/50'
                    }`}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Metric Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <FinancialMetricCard
            title="Open Enquiries"
            amount={activeEnquiries}
            currency=""
            icon={ClipboardList}
            progress={enquiries?.length ? Math.round((activeEnquiries / enquiries.length) * 100) : 0}
            trend="up"
            trendValue={`${enquiries?.length ?? 0} total`}
            color="primary"
          />
          <FinancialMetricCard
            title="Active Projects"
            amount={activeProjects}
            currency=""
            icon={Package}
            progress={projects?.length ? Math.round((activeProjects / projects.length) * 100) : 0}
            trend="up"
            trendValue={`${projects?.length ?? 0} total`}
            color="success"
          />
          <FinancialMetricCard
            title="Open Work Orders"
            amount={openWorkOrders}
            currency=""
            icon={Settings2}
            progress={workOrders?.length ? Math.round((openWorkOrders / workOrders.length) * 100) : 0}
            trend={openWorkOrders > 10 ? "down" : "up"}
            trendValue={`${workOrders?.length ?? 0} total`}
            color="warning"
          />
          <FinancialMetricCard
            title="Pending POs"
            amount={pendingPOs}
            currency=""
            icon={ShoppingCart}
            progress={purchaseOrders?.length ? Math.round((pendingPOs / purchaseOrders.length) * 100) : 0}
            trend={pendingPOs > 5 ? "down" : "up"}
            trendValue={`${purchaseOrders?.length ?? 0} total`}
            color="warning"
          />
          <FinancialMetricCard
            title="Total Revenue"
            amount={totalRevenue}
            icon={DollarSign}
            progress={revenueProgress}
            trend="up"
            trendValue={`${revenueProgress}% collected`}
            color="info"
          />
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Left Column - Charts */}
          <div className="lg:col-span-8 space-y-6">
            <TrendChart
              title={`Revenue vs Expense (${period})`}
              subtitle="Based on invoices and purchase orders"
              data={earningsData}
            />
            <ExpenseTracker title={`PO Spending — ${period === 'Daily' || period === 'Weekly' ? 'Last 7 Days' : period === 'Monthly' ? 'Last 4 Weeks' : 'Last 6 Months'}`} data={expenseData} />
          </div>

          {/* Right Column - Activities */}
          <div className="lg:col-span-4 space-y-6">
            <PaymentReminderCard />
            
            {/* Quick Stats */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 text-white">
                <div className="flex items-center gap-2 mb-2">
                  <FileText className="w-4 h-4 opacity-80" />
                  <span className="text-[11px] font-bold uppercase tracking-wider opacity-80">Quotations</span>
                </div>
                <div className="text-2xl font-black">{quotations?.length ?? 0}</div>
              </div>
              <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl p-4 text-white">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className="w-4 h-4 opacity-80" />
                  <span className="text-[11px] font-bold uppercase tracking-wider opacity-80">PO Value</span>
                </div>
                <div className="text-xl font-black">
                  ₹{(totalPOValue / 100000).toFixed(1)}L
                </div>
              </div>
            </div>

            <RecentActivityCard
              title="Recent Transactions"
              activities={recentActivities}
            />
            <OutstandingInvoicesWidget />
            <DocumentPipelineView />
            <StockMovementWidget />
          </div>
        </div>
      </div>
    </div>
  );
};
